if (!customElements.get('user-alliance-share')) {
  class UserAllianceShare extends HTMLElement {
    connectedCallback() {
      this.shareUrl = this.dataset.shareUrl || window.location.href;
      this.shareTitle = this.dataset.shareTitle || document.title;
      this.copiedText = this.dataset.copiedText || 'Copied';
      this.referralUrl = '';
      this.referralRequestId = 0;
      this.referralEndpoint = this.dataset.referralEndpoint || 'https://test-api.engweapp.cn/api/shopify/alliance/referral';
      this.emailEndpoint = this.dataset.emailEndpoint || 'https://test-api.engweapp.cn/api/shopify/alliance/email/send';
      this.authEmail = '';
      this.authStoreCode = '';
      this.syncAuthSession();
      this.inlineReferral = this.hasAttribute('data-inline-referral');
      this.inlineLink = this.querySelector('[data-ua-inline-link]');
      this.inlineStatus = this.querySelector('[data-ua-inline-status]');
      this.inlineRetry = this.querySelector('[data-ua-inline-retry]');

      this.shareButton = this.querySelector('[data-ua-share-button]');
      this.referralButton = this.querySelector('[data-ua-share-icon]');
      this.copyButtons = Array.from(this.querySelectorAll('[data-ua-copy]'));
      this.qrButton = this.querySelector('[data-ua-qr]');
      this.qrDialog = this.querySelector('[data-ua-qr-dialog]');
      this.qrImage = this.querySelector('[data-ua-qr-image]');
      this.qrCloseButton = this.querySelector('[data-ua-qr-close]');

      this.referralDialog = this.querySelector('[data-ua-referral-dialog]');
      this.referralCloseButton = this.querySelector('[data-ua-referral-close]');
      this.referralLinkPanel = this.querySelector('[data-ua-referral-link-panel]');
      this.referralLinkInput = this.querySelector('[data-ua-referral-link]');
      this.referralCopyButton = this.querySelector('[data-ua-referral-copy]');
      this.referralStatus = this.querySelector('[data-ua-referral-status]');
      this.emailPanel = this.querySelector('[data-ua-email-panel]');
      this.emailForm = this.querySelector('[data-ua-email-form]');
      this.friendEmailInput = this.querySelector('[data-ua-friend-email]');
      this.emailContentInput = this.querySelector('[data-ua-email-content]');
      this.emailConfirmInput = this.querySelector('[data-ua-email-confirm]');
      this.emailError = this.querySelector('[data-ua-email-error]');
      this.emailSubmitButton = this.querySelector('[data-ua-email-submit]');

      this.onShare = (event) => this.share(event.currentTarget);
      this.onOpenReferral = () => this.openReferral();
      this.onCopy = (event) => this.copy(event.currentTarget);
      this.onOpenQr = () => this.openQr();
      this.onCloseQr = () => this.closeQr();
      this.onQrDialogClick = (event) => {
        if (event.target === this.qrDialog) this.closeQr();
      };
      this.onCloseReferral = () => this.closeReferral();
      this.onReferralDialogClick = (event) => {
        if (event.target === this.referralDialog) this.closeReferral();
      };
      this.onReferralDialogClose = () => this.restoreFocus();
      this.onCopyReferral = () => this.copyReferralLink();
      this.onEmailSubmit = (event) => this.submitReferralEmail(event);
      this.onInlineRetry = () => this.loadInlineReferral();
      this.onAuthChange = () => {
        this.referralController?.abort();
        this.referralRequestId += 1;
        this.syncAuthSession();
        this.referralUrl = '';
        if (this.referralDialog?.open) this.closeReferral();
        if (this.qrDialog?.open) this.closeQr();
        if (this.inlineReferral) {
          this.shareUrl = '';
          if (this.inlineLink) this.inlineLink.value = '';
          this.setInlineReady(false);
          this.setInlineStatus('');
        }
        if (this.inlineReferral && this.authEmail) this.loadInlineReferral();
      };
      document.addEventListener('user-alliance:auth-change', this.onAuthChange);

      this.shareButton?.addEventListener('click', this.onShare);
      this.referralButton?.addEventListener('click', this.onOpenReferral);
      this.copyButtons.forEach((button) => button.addEventListener('click', this.onCopy));
      this.qrButton?.addEventListener('click', this.onOpenQr);
      this.qrCloseButton?.addEventListener('click', this.onCloseQr);
      this.qrDialog?.addEventListener('click', this.onQrDialogClick);
      this.referralCloseButton?.addEventListener('click', this.onCloseReferral);
      this.referralDialog?.addEventListener('click', this.onReferralDialogClick);
      this.referralDialog?.addEventListener('close', this.onReferralDialogClose);
      this.referralCopyButton?.addEventListener('click', this.onCopyReferral);
      this.emailForm?.addEventListener('submit', this.onEmailSubmit);
      this.inlineRetry?.addEventListener('click', this.onInlineRetry);
      if (this.inlineReferral && this.authEmail) this.loadInlineReferral();
    }

    disconnectedCallback() {
      this.shareButton?.removeEventListener('click', this.onShare);
      this.referralButton?.removeEventListener('click', this.onOpenReferral);
      this.copyButtons?.forEach((button) => button.removeEventListener('click', this.onCopy));
      this.qrButton?.removeEventListener('click', this.onOpenQr);
      this.qrCloseButton?.removeEventListener('click', this.onCloseQr);
      this.qrDialog?.removeEventListener('click', this.onQrDialogClick);
      this.referralCloseButton?.removeEventListener('click', this.onCloseReferral);
      this.referralDialog?.removeEventListener('click', this.onReferralDialogClick);
      this.referralDialog?.removeEventListener('close', this.onReferralDialogClose);
      this.referralCopyButton?.removeEventListener('click', this.onCopyReferral);
      this.emailForm?.removeEventListener('submit', this.onEmailSubmit);
      this.inlineRetry?.removeEventListener('click', this.onInlineRetry);
      document.removeEventListener('user-alliance:auth-change', this.onAuthChange);
      this.referralController?.abort();
      this.referralRequestId += 1;
      window.clearTimeout(this.copiedTimer);
      window.clearTimeout(this.referralCopyTimer);
    }

    async share(trigger) {
      if (navigator.share) {
        try {
          await navigator.share({ title: this.shareTitle, url: this.shareUrl });
          return;
        } catch (error) {
          if (error && error.name === 'AbortError') return;
        }
      }
      this.copy(trigger && trigger.hasAttribute('data-ua-share-button') ? trigger : null);
    }

    async copy(trigger) {
      if (this.inlineReferral && !this.shareUrl) return;
      try {
        await this.copyText(this.shareUrl);
        if (this.inlineReferral) this.setInlineStatus(this.copiedText);
        else this.showCopied(trigger);
      } catch (error) {
        if (this.inlineReferral) this.setInlineStatus('Unable to copy. Please select and copy the link.');
        else this.showCopied(trigger, false);
      }
    }

    async copyReferralLink() {
      if (!this.referralUrl || !this.referralCopyButton) return;

      try {
        await this.copyText(this.referralUrl);
        const originalLabel = this.referralCopyButton.dataset.originalLabel || this.referralCopyButton.textContent;
        this.referralCopyButton.dataset.originalLabel = originalLabel;
        this.referralCopyButton.textContent = this.copiedText;
        window.clearTimeout(this.referralCopyTimer);
        this.referralCopyTimer = window.setTimeout(() => {
          this.referralCopyButton.textContent = originalLabel;
        }, 2000);
        this.setReferralStatus(this.copiedText, 'success');
      } catch (error) {
        this.setReferralStatus('Unable to copy the referral link. Please select it and copy it manually.', 'error');
      }
    }

    async copyText(value) {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        return;
      }

      const input = document.createElement('input');
      input.value = value;
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      const copied = document.execCommand('copy');
      input.remove();
      if (!copied) throw new Error('Copy command failed');
    }

    showCopied(trigger, copied = true) {
      if (!trigger) return;
      const label = trigger.querySelector('[data-ua-copied]');
      if (!label) return;
      label.textContent = copied ? this.copiedText : 'Copy failed';
      label.hidden = false;
      window.clearTimeout(this.copiedTimer);
      this.copiedTimer = window.setTimeout(() => {
        label.hidden = true;
      }, 2000);
    }

    async openReferral() {
      if (!this.referralDialog) return;

      this.lastFocusedElement = document.activeElement;
      this.resetReferralState();
      this.showDialog(this.referralDialog);

      if (this.inlineReferral && this.shareUrl) {
        this.referralUrl = this.shareUrl;
        if (this.referralLinkInput) this.referralLinkInput.value = this.referralUrl;
        if (this.referralCopyButton) this.referralCopyButton.disabled = false;
        if (this.emailPanel) this.emailPanel.hidden = false;
        this.friendEmailInput?.focus({ preventScroll: true });
        return;
      }

      this.referralController?.abort();
      this.referralController = new AbortController();
      const requestId = ++this.referralRequestId;
      this.setReferralStatus('Loading your referral link...', 'loading');

      if (!this.authEmail) {
        this.setReferralStatus('Please log in to generate your referral link.', 'error');
        return;
      }

      try {
        const referralUrl = await this.requestReferralLink(this.referralController.signal);
        if (requestId !== this.referralRequestId) return;

        this.referralUrl = referralUrl;
        if (this.referralLinkInput) this.referralLinkInput.value = this.referralUrl;
        if (this.referralCopyButton) this.referralCopyButton.disabled = false;
        if (this.emailPanel) this.emailPanel.hidden = false;
        this.setReferralStatus('', '');
        this.friendEmailInput?.focus({ preventScroll: true });
      } catch (error) {
        if (error?.name === 'AbortError' || requestId !== this.referralRequestId) return;
        this.setReferralStatus(this.getRequestErrorMessage(error, 'Unable to load your referral link. Please try again.'), 'error');
      }
    }

    async requestReferralLink(signal) {
      const response = await window.UserAllianceAuth.request(this.referralEndpoint, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storeCode: this.authStoreCode,
          email: this.authEmail,
          name: ''
        }),
        signal
      });
      const data = await this.readResponse(response);
      if (!response.ok || (data?.code !== undefined && Number(data.code) !== 0)) {
        throw new Error('Unable to load your referral link. Please try again.');
      }
      const url = this.extractReferralUrl(data);
      if (!url) throw new Error('The referral link was not returned. Please try again.');
      return this.normaliseUrl(url);
    }

    async loadInlineReferral() {
      this.referralController?.abort();
      this.referralController = new AbortController();
      const requestId = ++this.referralRequestId;
      this.shareUrl = '';
      this.referralUrl = '';
      if (this.inlineLink) this.inlineLink.value = '';
      this.setInlineReady(false);
      this.inlineRetry.hidden = true;
      this.setAttribute('aria-busy', 'true');
      this.setInlineStatus('Loading your referral link...');
      try {
        if (!this.authEmail) throw new Error('Customer not logged in');
        const url = await this.requestReferralLink(this.referralController.signal);
        if (requestId !== this.referralRequestId) return;
        this.shareUrl = url;
        this.referralUrl = url;
        if (this.inlineLink) this.inlineLink.value = url;
        this.updateSocialLinks(url);
        this.qrImage?.removeAttribute('src');
        this.setInlineReady(true);
        this.setInlineStatus('');
      } catch (error) {
        if (error?.name === 'AbortError' || requestId !== this.referralRequestId) return;
        this.setInlineStatus('Unable to load your referral link. Please try again.');
        this.inlineRetry.hidden = false;
      } finally {
        if (requestId === this.referralRequestId) this.setAttribute('aria-busy', 'false');
      }
    }

    setInlineReady(ready) {
      this.querySelectorAll('[data-ua-copy], [data-ua-share-icon], [data-ua-qr]').forEach((button) => {
        button.disabled = !ready;
      });
      this.querySelectorAll('[data-ua-social]').forEach((link) => {
        if (ready) {
          link.removeAttribute('aria-disabled');
          link.removeAttribute('tabindex');
        } else {
          link.removeAttribute('href');
          link.setAttribute('aria-disabled', 'true');
          link.setAttribute('tabindex', '-1');
        }
      });
    }

    updateSocialLinks(referralUrl) {
      const endpoints = {
        facebook: 'https://www.facebook.com/sharer.php',
        twitter: 'https://twitter.com/intent/tweet',
        threads: 'https://www.threads.net/intent/post',
        whatsapp: 'https://api.whatsapp.com/send',
        email: 'mailto:'
      };
      this.querySelectorAll('[data-ua-social]').forEach((link) => {
        const network = link.dataset.uaSocial;
        if (!endpoints[network]) return;
        const url = new URL(endpoints[network]);
        if (network === 'facebook') url.searchParams.set('u', referralUrl);
        else if (network === 'twitter') {
          url.searchParams.set('text', this.shareTitle);
          url.searchParams.set('url', referralUrl);
        } else if (network === 'email') {
          url.searchParams.set('subject', this.shareTitle);
          url.searchParams.set('body', referralUrl);
        } else url.searchParams.set('text', `${this.shareTitle} ${referralUrl}`.trim());
        link.href = url.href;
      });
    }

    setInlineStatus(message) {
      if (!this.inlineStatus) return;
      this.inlineStatus.textContent = message;
      this.inlineStatus.hidden = !message;
    }

    syncAuthSession() {
      const current = window.UserAllianceAuth?.getSession() || {};
      this.authEmail = current.accessToken ? String(current.email || '').trim() : '';
      this.authStoreCode = current.accessToken ? String(current.storeCode || '').trim().toUpperCase() : '';
      if (this.authStoreCode === 'GB') this.authStoreCode = 'UK';
    }

    resetReferralState() {
      this.referralUrl = '';
      if (this.referralLinkPanel) this.referralLinkPanel.hidden = false;
      if (this.referralLinkInput) this.referralLinkInput.value = '';
      if (this.referralCopyButton) {
        this.referralCopyButton.disabled = true;
        this.referralCopyButton.textContent = this.referralCopyButton.dataset.originalLabel || this.referralCopyButton.textContent;
      }
      if (this.emailPanel) this.emailPanel.hidden = true;
      if (this.emailForm) this.emailForm.reset();
      this.clearEmailError();
      this.setReferralStatus('', '');
    }

    async submitReferralEmail(event) {
      event.preventDefault();
      if (!this.emailForm) return;
      this.clearEmailError();

      const email = this.friendEmailInput?.value.trim() || '';
      const content = this.emailContentInput?.value.trim() || '';
      if (!this.referralUrl) {
        this.setEmailError('Your referral link is still loading. Please wait a moment and try again.');
        return;
      }
      if (!this.isValidEmail(email)) {
        this.setEmailError('Enter a valid email address.', this.friendEmailInput);
        this.friendEmailInput?.focus();
        return;
      }
      if (!content) {
        this.setEmailError('Add a message before sending.', this.emailContentInput);
        this.emailContentInput?.focus();
        return;
      }
      if (!this.emailConfirmInput?.checked) {
        this.setEmailError('Please confirm the email address before sending.', this.emailConfirmInput);
        this.emailConfirmInput?.focus();
        return;
      }

      const message = content.includes(this.referralUrl) ? content : `${content}\n\n${this.referralUrl}`;
      this.emailSubmitButton && (this.emailSubmitButton.disabled = true);
      this.setReferralStatus('Sending your email...', 'loading');

      try {
        const response = await window.UserAllianceAuth.request(this.emailEndpoint, {
          method: 'POST',
          // mode: 'cors',
          // credentials: 'omit',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ toEmail: email, content: message })
        });
        const data = await this.readResponse(response);
        if (!response.ok) throw new Error(this.getResponseMessage(data) || 'Unable to send the email. Please try again.');

        this.emailForm.reset();
        this.setReferralStatus('Email sent successfully.', 'success');
      } catch (error) {
        this.setReferralStatus(this.getRequestErrorMessage(error, 'Unable to send the email. Please try again.'), 'error');
      } finally {
        if (this.emailSubmitButton) this.emailSubmitButton.disabled = false;
      }
    }

    isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }

    setEmailError(message, field) {
      if (!this.emailError) return;
      this.emailError.textContent = message;
      this.emailError.hidden = !message;
      field?.setAttribute('aria-invalid', 'true');
    }

    clearEmailError() {
      if (this.emailError) {
        this.emailError.textContent = '';
        this.emailError.hidden = true;
      }
      this.friendEmailInput?.removeAttribute('aria-invalid');
      this.emailContentInput?.removeAttribute('aria-invalid');
      this.emailConfirmInput?.removeAttribute('aria-invalid');
    }

    setReferralStatus(message, type) {
      if (!this.referralStatus) return;
      this.referralStatus.textContent = message;
      this.referralStatus.hidden = !message;
      this.referralStatus.classList.remove('is-loading', 'is-success', 'is-error');
      if (type) this.referralStatus.classList.add(`is-${type}`);
    }

    async readResponse(response) {
      const text = await response.text();
      if (!text) return {};
      try {
        return JSON.parse(text);
      } catch (error) {
        return text;
      }
    }

    getResponseMessage(data) {
      if (typeof data === 'string') return data;
      if (!data || typeof data !== 'object') return '';
      if (typeof data.message === 'string') return data.message;
      if (typeof data.error === 'string') return data.error;
      if (Array.isArray(data.errors)) return data.errors.join(', ');
      if (data.data && typeof data.data === 'object') return this.getResponseMessage(data.data);
      return '';
    }

    getRequestErrorMessage(error, fallback) {
      if (error?.name === 'TypeError') {
        return 'Unable to reach the referral service. Please check the API HTTPS and CORS settings.';
      }
      return error?.message || fallback;
    }

    extractReferralUrl(data) {
      if (typeof data === 'string') return /^(https?:\/\/|\/)/i.test(data.trim()) ? data.trim() : '';
      const keys = ['referralLink', 'referralUrl', 'shareUrl', 'share_url', 'link', 'url'];
      const visit = (value) => {
        if (typeof value === 'string' && /^(https?:\/\/|\/)/i.test(value.trim())) return value.trim();
        if (!value || typeof value !== 'object') return '';
        for (const key of keys) {
          const result = visit(value[key]);
          if (result) return result;
        }
        for (const nested of Object.values(value)) {
          const result = visit(nested);
          if (result) return result;
        }
        return '';
      };
      return visit(data);
    }

    normaliseUrl(value) {
      const url = new URL(value, window.location.origin);
      if (!['http:', 'https:'].includes(url.protocol)) throw new Error('Invalid referral link');
      return url.href;
    }

    showDialog(dialog) {
      if (dialog.open) return;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }

    openQr() {
      if (!this.qrDialog) return;
      if (this.qrImage && !this.qrImage.getAttribute('src')) {
        this.qrImage.src = 'https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=' + encodeURIComponent(this.shareUrl);
      }
      this.showDialog(this.qrDialog);
    }

    closeQr() {
      if (!this.qrDialog) return;
      if (typeof this.qrDialog.close === 'function') this.qrDialog.close();
      else this.qrDialog.removeAttribute('open');
    }

    closeReferral() {
      if (!this.referralDialog) return;
      this.referralRequestId += 1;
      this.referralController?.abort();
      if (typeof this.referralDialog.close === 'function') this.referralDialog.close();
      else {
        this.referralDialog.removeAttribute('open');
        this.restoreFocus();
      }
    }

    restoreFocus() {
      this.lastFocusedElement?.focus?.({ preventScroll: true });
      this.lastFocusedElement = null;
    }
  }

  customElements.define('user-alliance-share', UserAllianceShare);
}
